import { AxiosResponse, AxiosRequestConfig } from 'axios'

import { Configuration, Contact, Api, Group, GetContactsQuery, PostContactsBody, Response, WithPagination } from '@/shared/types/api'
import { contactsQueryToString } from '@/shared/utils'
import { getAppURL } from '@/shared/utils/electron'
import { useFetchApi, Request } from '@/shared/utils/fetch'

export function useApi (): Api {
  const useFetch = useFetchApi()

  return {
    guestLogin: (
      login: string,
      roomToken: string,
    ): Promise<AxiosResponse<string>> => {
      const bodyData = {
        login,
        roomToken,
      }
      return useFetch.post(getAppURL({ pathName: '/api/guestIMClient/token' }), bodyData)
    },

    getContacts: function (query: GetContactsQuery, controller?: AbortController): Promise<Response<WithPagination<Contact[]>>> {
      const params = contactsQueryToString(query)

      const options: Request & AxiosRequestConfig = {}
      if (controller) {
        options.signal = controller.signal
      }

      return useFetch.get(getAppURL({ pathName: `/api/user/contacts?${params}` }), options)
    },

    getConfiguration: function (): Promise<Response<Configuration>> {
      return useFetch.get<Configuration>(getAppURL({ pathName: '/api/user/configuration' }))
    },

    getConfigurationGuest: (): Promise<Response<Configuration>> => {
      return useFetch.get(getAppURL({ pathName: '/api/guestimclient/configuration' }))
    },

    getWhoAmI: function <T = Contact> (): Promise<Response<T>> {
      return useFetch.get<T>(getAppURL({ pathName: '/api/user/who-am-i' }))
    },

    getGroups: function (): Promise<Response<Group[]>> {
      return useFetch.get<Group[]>(getAppURL({ pathName: '/api/user/groups' }))
    },

    addUserGroup: function (name: string): Promise<Response<Group>> {
      return useFetch.post<Group>(getAppURL({ pathName: '/api/user/groups' }), { name, type: 'user' })
    },

    addFavoriteContact: function (
      ContactId: string,
    ): Promise<Response<void>> {
      return useFetch.post(getAppURL({ pathName: `/api/user/favorite?id=${ContactId}` }))
    },

    removeFavoriteContact: function (
      ContactId: string,
    ): Promise<Response<void>> {
      return useFetch.delete(getAppURL({ pathName: `/api/user/favorite?id=${ContactId}` }))
    },
    /*
    * Body для внешних контактов:
    * {
    *   "groupId": "string",
    *   "name": "string",
    *   "internalNumber": "string",
    *   "organization": "string",
    *   "organizationalUnit": "string",
    *   "position": "string",
    *   "mobilePhone": "string",
    *   "email": "string",
    *   "bDay": "2024-12-17T12:30:50.298Z",
    *   "contactType": "externalContacts"
    * }
    *
    * Body для абонентов:
    * {
    *   "groupId": "string",
    *   "guid": "string", - guid существующего юзера
    *   "contactType": "user"
    * }
    */
    addUserContact: function (
      contact: PostContactsBody,
    ): Promise<Response<Contact>> {
      return useFetch.post(getAppURL({ pathName: '/api/user/contacts' }), contact)
    },

    /*
    * В query обязательно слать параметр groupGuid, с гуидом группы, в рамках которой идет удаление контакта.
    * Если по гуиду будет найден внешний контакт, то из базы он удалится
    * Если это будет гуид юзера, то удалится привязка юзера к группе.
    */
    removeUserContact: function (
      guid: string,
      groupGuid: string,
    ): Promise<Response<void>> {
      return useFetch.delete(getAppURL({ pathName: `/api/user/contacts/${guid}` }), { params: { groupGuid }})
    },

    /*
    * см. Body в addUserContact
    */
    editUserContact: function (
      contact: Contact,
    ): Promise<Response<Contact>> {
      const guid = contact.guid || contact.id
      return useFetch.put(getAppURL({ pathName: `/api/user/contacts/${guid}` }), contact)
    },

    editUserContactPhoto: function (
      guid: string,
      photo: Blob,
    ): Promise<Response<void>> {
      const form = new FormData()
      form.append('file', photo)

      return useFetch.post(getAppURL({ pathName: `/api/user/contacts/${guid}/photo` }), form, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
    },

    removeUserContactPhoto: function (
      guid: string,
    ): Promise<Response<void>> {
      return useFetch.delete(getAppURL({ pathName: `/api/user/contacts/${guid}/photo` }))
    },
  }
}
